/**
 * DependencyPicker - Searchable dropdown for selecting dependencies.
 *
 * Displays a list of DependencyChips for selected dependencies and provides
 * a grouped, searchable dropdown for adding new ones. Uses portal rendering
 * to avoid z-index issues.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search } from 'lucide-react';
import { DependencyChip, SourceIdBadge } from '@/components/atoms';

export interface PredecessorInfo {
  teamName: string;
  teamColor?: string;
  deliverableName: string;
  featureName?: string;
  priority?: number;
  /** External tracker key; distinguishes same-named slices of one epic. */
  sourceId?: string | null;
}

/**
 * Deliverables grouped by team for the dropdown. Exported as the single source
 * of truth — `DeliverableForm` builds values of this shape and passes them as
 * `availableGroups`, so both sides must agree on the (optional) `sourceId`.
 */
export interface GroupedPredecessors {
  teamName: string;
  deliverables: { id: string; deliverableName: string; sourceId?: string | null }[];
  isCurrentTeam: boolean;
}

export interface DependencyPickerProps {
  /** Currently selected predecessor IDs (sorted) */
  selectedPredecessors: string[];
  /** Lookup map from predecessor ID to display info */
  predecessorLookup: Record<string, PredecessorInfo>;
  /** Grouped predecessors available for selection (already filtered, with isCurrentTeam flag) */
  availableGroups: GroupedPredecessors[];
  /** Total count of filtered predecessors (for empty state message) */
  filteredCount: number;
  /** Called when a predecessor is added */
  onAdd: (predecessorId: string) => void;
  /** Called when a predecessor is removed */
  onRemove: (predecessorId: string) => void;
  /** Optional callback when a dependency chip is clicked */
  onChipClick?: (predecessorId: string, teamName: string) => void;
  /** Label for the add button */
  addLabel?: string;
  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * DependencyPicker - Grouped, searchable dependency selector.
 *
 * @example
 * ```tsx
 * <DependencyPicker
 *   selectedPredecessors={['uuid-1', 'uuid-2']}
 *   predecessorLookup={lookupMap}
 *   availableGroups={groupedOptions}
 *   filteredCount={10}
 *   currentTeamName="Engineering"
 *   onAdd={handleAdd}
 *   onRemove={handleRemove}
 * />
 * ```
 */
export function DependencyPicker({
  selectedPredecessors,
  predecessorLookup,
  availableGroups,
  filteredCount,
  onAdd,
  onRemove,
  onChipClick,
  addLabel = 'Add',
  className,
}: DependencyPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter groups by search query
  const searchFilteredGroups = useMemo((): GroupedPredecessors[] => {
    if (!searchQuery.trim()) return availableGroups;

    const query = searchQuery.toLowerCase();
    return availableGroups
      .map((group) => ({
        ...group,
        deliverables: group.deliverables.filter(
          (d) =>
            d.deliverableName.toLowerCase().includes(query) ||
            group.teamName.toLowerCase().includes(query) ||
            (d.sourceId?.toLowerCase().includes(query) ?? false),
        ),
      }))
      .filter((group) => group.deliverables.length > 0);
  }, [availableGroups, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    }
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  const handleAdd = (predecessorId: string) => {
    onAdd(predecessorId);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Helper functions for display
  const getDisplayName = (predecessorId: string) => {
    const info = predecessorLookup[predecessorId];
    if (!info) return predecessorId;
    const prefix = info.priority != null ? `#${info.priority} ` : '';
    return `${prefix}${info.deliverableName}`;
  };

  const getTooltipText = (predecessorId: string) => {
    const info = predecessorLookup[predecessorId];
    if (!info) return predecessorId;

    const parts: string[] = [];
    if (info.featureName) parts.push(info.featureName);
    parts.push(info.teamName);
    parts.push(info.deliverableName);
    return parts.join(' | ');
  };

  const getPredecessorColor = (predecessorId: string) => {
    const info = predecessorLookup[predecessorId];
    return info?.teamColor || '#64748b';
  };

  return (
    <>
      {/* Selected Dependencies + Add Button - all in one row */}
      <div
        className={`
          flex flex-wrap items-center gap-1
          ${className || ''}
        `}
      >
        {selectedPredecessors.map((pred) => {
          const predInfo = predecessorLookup[pred];
          return (
            <DependencyChip
              key={pred}
              dependencyId={pred}
              name={getDisplayName(pred)}
              teamColor={getPredecessorColor(pred)}
              maxWidth="none"
              tooltip={getTooltipText(pred)}
              sourceId={predInfo?.sourceId}
              onClick={onChipClick && predInfo ? () => onChipClick(pred, predInfo.teamName) : undefined}
              onRemove={() => onRemove(pred)}
            />
          );
        })}

        {/* Add Button - inline with chips */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="
            flex items-center gap-0.5 text-xs text-text-muted transition-colors
            hover:text-action-primary
            dark:text-text-muted
            dark:hover:text-text-accent
          "
        >
          <Plus className="size-3" />
          {addLabel}
        </button>
      </div>

      {/* Dropdown Portal */}
      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="
              overflow-hidden rounded-lg border border-border-strong bg-bg-elevated shadow-xl
              dark:border-border-strong dark:bg-bg-elevated
            "
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: 288,
              zIndex: 99999,
            }}
          >
            {/* Search Input */}
            <div
              className="
                border-b border-border-strong p-2
                dark:border-border-strong
              "
            >
              <div className="relative">
                <Search
                  className="
                    absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-text-subtle
                    dark:text-text-muted
                  "
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search deliverables..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="
                    w-full rounded-sm border border-border-strong bg-transparent py-1.5 pr-2 pl-7 text-xs
                    text-text-primary
                    placeholder:text-text-muted
                    focus:ring-1 focus:ring-primary-500 focus:outline-hidden
                    dark:border-border-strong dark:text-text-primary
                  "
                />
              </div>
            </div>

            {/* Results List */}
            <div className="max-h-60 overflow-y-auto">
              {searchFilteredGroups.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-text-muted">
                  {filteredCount === 0 ? 'No available dependencies' : 'No matches found'}
                </div>
              ) : (
                searchFilteredGroups.map((group) => (
                  <div key={group.teamName}>
                    {/* Team Header */}
                    <div
                      className="
                        sticky top-0 bg-bg-elevated px-3 py-1.5 text-xs font-medium text-text-muted
                        dark:bg-bg-elevated dark:text-text-muted
                      "
                    >
                      {group.teamName}
                      {group.isCurrentTeam && (
                        <span
                          className="
                            ml-1 text-action-primary
                            dark:text-text-accent
                          "
                        >
                          (current)
                        </span>
                      )}
                    </div>
                    {/* Deliverables */}
                    {group.deliverables.map((deliverable) => (
                      <button
                        key={deliverable.id}
                        type="button"
                        onClick={() => handleAdd(deliverable.id)}
                        className="
                          flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs
                          text-text-secondary transition-colors
                          hover:bg-bg-muted
                          dark:text-text-muted
                          dark:hover:bg-bg-subtle
                        "
                      >
                        <span className="truncate font-medium">{deliverable.deliverableName}</span>
                        {deliverable.sourceId && (
                          <SourceIdBadge
                            sourceId={deliverable.sourceId}
                            variant="compact"
                            size="sm"
                            className="shrink-0"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
